import { useEffect, useState } from 'react';
import { devError } from '../../lib/devLog';

// Encapsulates the browser SpeechRecognition lifecycle for the admin
// artwork-description textarea. Streams interim + final transcripts back
// into the caller via `onTranscript`, and exposes a single `toggle()` that
// flips listening on/off.
//
// Returns `{ isListening, toggle, supported }`. When `supported === false`
// the caller should hide the mic button (or show a friendly fallback) —
// only Chromium-family browsers ship the API today.
const useSpeechToText = (onTranscript) => {
  const [recognition, setRecognition] = useState(null);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    const SR = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    if (!SR) return undefined;

    const instance = new SR();
    instance.continuous = true;
    instance.interimResults = true;

    instance.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join('');
      onTranscript(transcript);
    };
    instance.onerror = (event) => {
      devError('Speech recognition error:', event.error);
      setIsListening(false);
    };

    setRecognition(instance);
    return () => {
      try { instance.stop(); } catch (err) {
        // Recognition already stopped or unmounted mid-listen — safe to ignore.
        devError('[speech] cleanup stop failed (already stopped):', err?.message);
      }
    };
    // onTranscript is stable from the caller; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    if (!recognition) {
      // eslint-disable-next-line no-alert
      alert('Voice recognition not supported in this browser');
      return;
    }
    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  return { isListening, toggle, supported: !!recognition };
};

export default useSpeechToText;
